import { apiService } from "../../../../components/api/apiService";

const BASE = "/api/MCPResources";

export interface MCPResourceRow {
  id: string;
  resourceId: string;
  name: string;
  cat: string;
  type: string;
  isActive: boolean;
  createdAt: string;
  description?: string;
  updatedAt?: string;
  /** Present when API returns schema (list or GET by id). */
  resourceSchema?: string;
  dataModelId?: string;
}

export interface RegisterMCPResourcePayload {
  name: string;
  category: string;
  description: string;
  /** RFC 4122 UUID; required by API on create (generated in the client). */
  resourceId?: string;
  /** Selected data-model or knowledge-base id from the Resource dropdown. */
  dataModelId?: string;
  resourceSchema: string;
  isActive: boolean;
}

export interface MCPDataModelOption {
  id: string;
  modelName: string;
  description: string;
  type: string;
  tags: string[];
  isActive: boolean;
  status: string;
  schema: unknown;
}

function normalizeDataModel(raw: Record<string, unknown>): MCPDataModelOption {
  const pick = (k: string, kAlt?: string) => {
    const v = raw[k] ?? (kAlt ? raw[kAlt] : undefined);
    return v == null ? "" : String(v);
  };
  const tagsRaw = raw.tags ?? raw.Tags;
  const tags = Array.isArray(tagsRaw) ? tagsRaw.map((t) => String(t)) : [];
  const schema = raw.schema ?? raw.Schema;
  return {
    id: pick("id", "Id"),
    modelName: pick("modelName", "ModelName"),
    description: pick("description", "Description"),
    type: pick("type", "Type"),
    tags,
    isActive: Boolean(raw.isActive ?? raw.IsActive),
    status: pick("status", "Status"),
    schema: schema ?? null,
  };
}

function mapFromApi(res: Record<string, unknown>): MCPResourceRow {
  const pick = (k: string, kAlt?: string) => {
    const v = res[k] ?? (kAlt ? res[kAlt] : undefined);
    return v == null ? "" : String(v);
  };
  const toDateStr = (v: unknown) => {
    if (v == null || v === "") return "";
    const s = String(v);
    return s.length >= 10 ? s.slice(0, 10) : new Date(s).toISOString().slice(0, 10);
  };
  const descRaw = res.description ?? res.Description;
  const desc = descRaw != null && String(descRaw).trim() ? String(descRaw) : undefined;
  const upd = res.updatedAt ?? res.UpdatedAt;
  const rsRaw = res.resourceSchema ?? res.ResourceSchema;
  let resourceSchema: string | undefined;
  if (rsRaw != null && rsRaw !== "") {
    resourceSchema =
      typeof rsRaw === "string" ? rsRaw : JSON.stringify(rsRaw as object, null, 2);
  }
  const dm =
    res.dataModelId ?? res.DataModelId ?? res.DataModelID ?? res.dataModelID;
  const dataModelId = dm != null && String(dm).trim() ? String(dm) : undefined;
  return {
    id: pick("id", "Id"),
    resourceId: pick("resourceId", "ResourceId"),
    name: pick("name", "Name"),
    cat: pick("category", "Category") || "—",
    type: pick("type", "Type") || "—",
    isActive: Boolean(res.isActive ?? res.IsActive),
    createdAt: toDateStr(res.createdAt ?? res.CreatedAt) || "",
    description: desc,
    updatedAt: upd != null && upd !== "" ? toDateStr(upd) : undefined,
    resourceSchema,
    dataModelId,
  };
}

function buildRegisterBody(payload: RegisterMCPResourcePayload): Record<string, unknown> {
  const body: Record<string, unknown> = {
    name: payload.name,
    category: payload.category,
    description: payload.description,
    resourceSchema: payload.resourceSchema,
    isActive: payload.isActive,
  };
  const rid = payload.resourceId?.trim();
  if (rid) body.resourceId = rid;
  const dm = payload.dataModelId?.trim();
  if (dm) body.dataModelId = dm;
  return body;
}

export const mcpResourcesService = {
  async getAll(): Promise<MCPResourceRow[]> {
    const data = await apiService.get(BASE, {}, "backend");
    const list = data?.resources ?? data?.Resources ?? [];
    if (!Array.isArray(list)) return [];
    return list.map((item: Record<string, unknown>) => mapFromApi(item));
  },

  async register(payload: RegisterMCPResourcePayload): Promise<MCPResourceRow> {
    const res = await apiService.post(BASE, buildRegisterBody(payload), "backend");
    return mapFromApi(res as Record<string, unknown>);
  },

  /** Full update (same shape as create). */
  async updateResource(id: string, payload: RegisterMCPResourcePayload): Promise<MCPResourceRow | null> {
    const res = await apiService.put(`${BASE}/${id}`, buildRegisterBody(payload), "backend");
    return res ? mapFromApi(res as Record<string, unknown>) : null;
  },

  async getById(id: string): Promise<MCPResourceRow | null> {
    try {
      const data = await apiService.get(`${BASE}/${id}`, {}, "backend");
      const raw = data?.resource ?? data?.Resource ?? data;
      if (!raw || typeof raw !== "object") return null;
      return mapFromApi(raw as Record<string, unknown>);
    } catch {
      return null;
    }
  },

  async getDataModels(): Promise<MCPDataModelOption[]> {
    const data = await apiService.get(`${BASE}/data-models`, {}, "backend");
    const list = data?.dataModels ?? data?.DataModels ?? [];
    if (!Array.isArray(list)) return [];
    return list.map((item: Record<string, unknown>) => normalizeDataModel(item));
  },

  async update(id: string, payload: { isActive?: boolean }): Promise<MCPResourceRow | null> {
    const res = await apiService.put(`${BASE}/${id}`, payload, "backend");
    return res ? mapFromApi(res) : null;
  },

  async delete(id: string): Promise<void> {
    await apiService.delete(`${BASE}/${id}`, "backend");
  },

  /** Number of developer tokens that have this resource assigned (for delete warning). */
  async getAssignedTokenCount(id: string): Promise<{ assignedTokenCount: number }> {
    const data = await apiService.get(`${BASE}/${id}/assigned-token-count`, {}, "backend");
    return { assignedTokenCount: data?.assignedTokenCount ?? 0 };
  },
};
